import React, { ReactNode, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ShareMode } from '@/types/api';
import useClickOutside from '@/components/Record/SubjectContent/@hooks/useClickOutside';
import ArrowTop from '@/public/icons/record/arrow_top.svg';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import usePlatform from '@/components/Record/hooks/usePlatform';
import MobileBottomSheet, {
  MobileBottomSheetRef,
} from '@/components/@IntranetLibrary/MobileBottomSheet';
import ShareIcon from '@/public/icons/record/share2.svg';
import CommentIcon from '@/public/icons/record/comment.svg';
import ShareDeleteIcon from '@/public/icons/record/shareDelete.svg';

export type ShareDetail = {
  mode: ShareMode;
  icon: ReactNode;
  title: string;
  subTitle: string;
};

export const defaultState: ShareDetail[] = [
  {
    mode: ShareMode.Share,
    icon: <ShareIcon />,
    title: '공유 허용',
    subTitle: '댓글 작성 및 공유 대상을 추가하거나 삭제할 수 있습니다.',
  },
  {
    mode: ShareMode.Comment,
    icon: <CommentIcon />,
    title: '댓글 허용',
    subTitle: '문서 조회 및 댓글을 작성할 수 있습니다.',
  },
  {
    mode: ShareMode.Delete,
    icon: <ShareDeleteIcon />,
    title: '공유 대상에서 제외',
    subTitle: '이 문서에 대한 모든 권한이 없어집니다.',
  },
];

type P = {
  shareMode: ShareMode;
  onClick: (org: ShareMode) => void;
  inviteMode?: boolean;
  disabled?: boolean;
  /** 변경할 수 있는 옵션 (ex. 어떤 경우에는 '제외'옵션이 필요없어서 해당 property를 추가) */
  modeOptions?: ShareMode[];
  style?: React.CSSProperties;
};

const ShareDetailButton = ({
  shareMode,
  onClick,
  inviteMode,
  disabled,
  modeOptions = defaultState.map((item) => item.mode),
  style,
}: P) => {
  const { t } = useTranslation();
  const { isMobilePath } = usePlatform();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<MobileBottomSheetRef>(null);
  useClickOutside(ref, () => setOpen(false));

  const modeState = defaultState.filter((item) =>
    modeOptions.includes(item.mode),
  );

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobilePath) {
      drawerRef.current?.open();
    } else {
      setAnchorEl(event.currentTarget);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleDrawerItemClick = (mode: ShareMode) => {
    onClick(mode);
    drawerRef.current?.close();
  };

  const id = open ? 'simple-popover' : undefined;

  if (shareMode === ShareMode.Owner) {
    return <StyledCreator>{t('작성자')}</StyledCreator>;
  }

  const handleItemClick = (mode: ShareMode) => {
    onClick(mode);
    handleClose();
  };

  const renderContent = () => (
    <>
      {modeState.map((item, index) => {
        const isDelete = index === 2;
        if (inviteMode && isDelete) return null;
        return (
          <React.Fragment key={item.title}>
            {isDelete && (
              <div
                style={{
                  height: '1px',
                  width: '100%',
                  backgroundColor: 'var(--dk-bg-strong, #e7e9ec)',
                }}
              ></div>
            )}
            <StyledDetailButton
              onClick={() =>
                isMobilePath
                  ? handleDrawerItemClick(item.mode)
                  : handleItemClick(item.mode)
              }
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <StyledTitle style={{ color: isDelete ? '#F55060' : '' }}>
                    {t(item.title)}
                  </StyledTitle>
                  <StyledDetail>{t(item.subTitle)}</StyledDetail>
                </div>
              </div>
            </StyledDetailButton>
          </React.Fragment>
        );
      })}
    </>
  );

  return (
    <>
      <StyledButton
        ref={ref}
        aria-describedby={id}
        onClick={handleClick}
        inviteMode={inviteMode}
        disabled={disabled}
        style={style}
      >
        {t(defaultState[shareMode].title)}
        <ArrowTop
          style={{
            transform: open ? '' : 'rotate(180deg)',
            color: inviteMode ? 'black' : disabled ? '#D1D8E2' : '#888d96',
          }}
        />
      </StyledButton>
      {isMobilePath ? (
        <MobileBottomSheet ref={drawerRef} height="auto" showHandle={false}>
          <MobileContainer>{renderContent()}</MobileContainer>
        </MobileBottomSheet>
      ) : (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid black',
            },
          }}
        >
          <Container>{renderContent()}</Container>
        </Popover>
      )}
    </>
  );
};
export default ShareDetailButton;

const StyledDetailButton = styled.button`
  :hover {
    background-color: var(--dk-bg-subtle, #f9fafb);
  }
  text-align: left;
`;

const StyledTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const StyledDetail = styled.div`
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: var(--dk-text-secondary, #888d96);
`;

const StyledButton = styled.button<{ inviteMode?: boolean }>`
  margin-left: 12px;
  display: flex;
  border-radius: 4px;
  border: ${({ inviteMode }) => (inviteMode ? '1px solid #e7e9ec' : 'none')};
  background-color: ${({ inviteMode }) => (inviteMode ? 'var(--dk-bg-subtle, #f9fafb)' : 'none')};
  color: ${({ inviteMode }) => (inviteMode ? 'var(--dk-text, black)' : 'var(--dk-text-secondary, #888d96)')};
  align-items: center;
  justify-content: center;
  padding: 4px 4px 4px 8px;
  font-weight: 400;
  font-size: ${({ inviteMode }) => (inviteMode ? '14px' : '12px')};
  line-height: 20px;
  position: relative;
  white-space: nowrap;
  :disabled {
    color: var(--dk-text-disabled, #d1d8e2);
    cursor: auto;
  }
`;

const Container = styled.div`
  width: max-content;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: var(--dk-bg, white);
  z-index: 2;
`;

const MobileContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: 20px 20px 48px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: var(--dk-bg, white);
`;

const StyledCreator = styled.div`
  color: var(--dk-accent, #06c);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 150% */
`;
